import { MessageType } from './message';

/**
 * Minimum drag distance in pixels required to trigger drawer state changes
 * Used to differentiate between taps and intentional drags
 */
export const DRAG_THRESHOLD_PX = 50;

/**
 * Maximum drag distance in % of viewport height for minimized and open states
 */
export const MINIMIZED_DRAG_MAX = 40;
export const OPEN_DRAG_MAX = 70;

/**
 * Time in milliseconds to wait before considering a touch a drag rather than a tap
 */
export const TAP_DURATION_MS = 200;

/**
 * The three resting positions of the drawer.
 *
 * NOTE the units are not uniform, and callers must not assume they are. OPEN and CLOSED are
 * percentages of viewport height, resolved through `toViewportPx` in useDraggableDrawer.
 * MINIMIZED is an already-resolved CSS length, passed through verbatim (useDraggableDrawer.js
 * branches on drawerState precisely to skip the percentage conversion for it) — the peek is a
 * fixed-size strip sized to its content, not a fraction of the viewport.
 *
 * MINIMIZED must fit the drag handlebar plus one row of suggested-question chips. Nothing
 * else renders in the peek (see DrawerContent), and the container is `sui-overflow-y-hidden`,
 * so anything that does not fit is clipped rather than scrolled.
 */
export const DRAWER_HEIGHTS = {
  OPEN: 100,
  MINIMIZED: '90px',
  CLOSED: 0
};

export const NR = {
  LOAD: 'MAA_LOADER_LOAD',
  OPEN: 'MAA_LOADER_OPEN',
  CLOSE: 'MAA_LOADER_CLOSE',
  LOADER_TIME: 'MAA_LOADER_TIME'
};

export const USER_QUESTION_CHARACTER_LIMIT = 250;

export const keyPressMap = { Enter: false, Shift: false };

export const FALLBACK_SESSION_ID = 'fallback-sessionId';
export const FALLBACK_USER_ID = 'fallback-userId';

export const MESSAGE_ORIGIN_PREPOPULATED = 'prepopulated';
export const MESSAGE_ORIGIN_CUSTOM = 'custom';
export const MESSAGE_ORIGIN_UNSPECIFIED = 'unspecified';
export const MESSAGE_ORIGIN_RETRY = 'retry';

export const SUBMIT_ANALYTICS_MAA_PRIMARY_CATEGORY = 'magic apron';
export const IMAGE_UPLOAD_ANALYTICS_EVENT_NAME = 'maa search';
export const ASK_A_QUESTION_ANALYTICS_PLACEHOLDER = 'ask a question clicked';
export const FEEDBACK_ANALYTICS_MAA_PRIMARY_CATEGORY = 'magic apron';
export const FEEDBACK_ANALYTICS_MAA_EVENT_NAME_PREFIX = 'maa feedback';
export const FEEDBACK_ANALYTICS_QNA_PRIMARY_CATEGORY = 'magic apron';
export const FEEDBACK_ANALYTICS_QNA_EVENT_NAME_PREFIX = 'ma qna feedback';
export const FEEDBACK_CLICK_COMMENT_QUESTION_ORIGIN_MAA = 'maa';
export const FEEDBACK_CLICK_COMMENT_QUESTION_ORIGIN_QNA = 'ma qna';
export const FEEDBACK_CLICK_UPLOAD_CAMERA_ATTACHMENT = 'camera upload';
export const FEEDBACK_CLICK_UPLOAD_FILE_ATTACHMENT = 'file upload';
export const FEEDBACK_CLICK_UPLOAD_IMAGE_ATTACHMENT = 'image upload';
// LIVE_AGENT_ANALYTICS_* moved to liveAgent/constants.js (Seq C.3); LIVE_AGENT_*_MESSAGE
// stays here. See .claude/context/live-agent.md

export const RETRY_MESSAGE = {
  type: MessageType.BOT,
  content: {
    responseSummary: {
      summaryAnswer: 'The conversation was interrupted before I could finish finding the answer.',
    },
  },
};

// Live Agent system-message payloads (see the live-agent.md pointer above).
export const AGENT_HANDOFF_KEYWORDS = ['agent', 'associate'];

export const LIVE_AGENT_CONNECTING_MESSAGE = {
  type: MessageType.SYSTEM,
  variant: 'connecting',
  content: 'Connecting to a live associate',
};

export const LIVE_AGENT_CONNECTED_MESSAGE = {
  type: MessageType.SYSTEM,
  variant: 'connected',
  content: 'You\'re chatting with an associate',
  dividerText: 'Connected',
};

export const LIVE_AGENT_ENDED_MESSAGE = {
  type: MessageType.SYSTEM,
  variant: 'ended',
  content: 'You\'re chatting with Magic Apron',
  dividerText: 'Live chat ended',
};

export const LIVE_AGENT_IDLE_DISCONNECTED_MESSAGE = {
  type: MessageType.SYSTEM,
  variant: 'idle-ended',
  content: 'You\'re chatting with Magic Apron',
  dividerText: 'Live chat ended due to inactivity',
};

export const LIVE_AGENT_ERROR_MESSAGE = {
  type: MessageType.SYSTEM,
  content: 'Unable to connect to an associate right now. Please try again later.',
};

export const VALID_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];
export const MAX_FILE_SIZE_MB = 20;

export const UPLOAD_ERROR_MESSAGES = {
  UNSUPPORTED_CONTENT_PROPRIETY: 'This image doesn\'t meet content guidelines. Select a different image and try again.',
  FILE_SIZE_EXCEEDED: `This file is too large. Choose a file ${MAX_FILE_SIZE_MB} MB or less and try again.`,
  UNSUPPORTED_FILE_TYPE: 'This file type is not supported. Please upload a valid file.',
  GENERIC_ERROR: 'Something went wrong with the upload. Try again later.'
};

export const DEFAULT_MESSAGE_HISTORY_CONTAINER_WIDTH = 400;
export const IMAGE_SKELETON_PLACEHOLDER_MAX_WIDTH_PROPORTION = 0.8;
export const IMAGE_SKELETON_PLACEHOLDER_MAX_HEIGHT_PROPORTION = 0.5;
